import React, { useState } from 'react';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';

const TYPE_META = {
  post_meeting: { icon: '🤝', accent: '#4f86c6' },
  commitment: { icon: '📋', accent: '#f59e0b' },
  email_no_reply: { icon: '✉️', accent: '#9b59b6' },
  proposal: { icon: '📄', accent: '#E96A6A' },
};

/**
 * FollowUpPrompt
 *
 * Props:
 *   prompt            — prompt object from followUpService (may include target: { view, taskId })
 *   onDismiss()       — "Dismiss for now"
 *   onSnooze()        — "Remind me tomorrow"
 *   onMarkSent(taskId)— marks task complete (for proposal "Mark as sent")
 *   onOpenTask(taskId)— opens the task detail panel
 *   onNavigate(view)  — navigates to a top-level view (setFilter)
 */
export default function FollowUpPrompt({ prompt, onDismiss, onSnooze, onMarkSent, onOpenTask, onNavigate }) {
  const [confirming, setConfirming] = useState(false);
  const meta = TYPE_META[prompt.type] || TYPE_META.commitment;
  const hasTarget = !!prompt.target;

  function handleCardClick() {
    if (!hasTarget) return;
    const { view, taskId } = prompt.target;
    if (taskId && onOpenTask) {
      onOpenTask(taskId);
    } else if (view && onNavigate) {
      onNavigate(view);
    }
  }

  function handlePrimary(e) {
    e.stopPropagation();
    const { type, payload } = prompt.primaryAction;

    if (type === 'mailto') {
      const to = encodeURIComponent(payload.to || '');
      const subject = encodeURIComponent(payload.subject || '');
      window.open(`mailto:${to}?subject=${subject}`, '_blank');
      onDismiss();
    } else if (type === 'open_task') {
      onOpenTask?.(payload.taskId);
      onDismiss();
    } else if (type === 'mark_sent') {
      onMarkSent?.(payload.taskId);
      onDismiss();
    }
  }

  function handleSecondary(e) {
    e.stopPropagation();
    if (!prompt.secondaryAction) return;
    const { type, payload } = prompt.secondaryAction;
    if (type === 'mark_sent') {
      onMarkSent?.(payload.taskId);
      onDismiss();
    }
  }

  function stopProp(e) { e.stopPropagation(); }

  return (
    <div
      onClick={hasTarget ? handleCardClick : undefined}
      title={hasTarget ? 'Click to open' : undefined}
      style={{
        background: '#fff',
        border: `1px solid ${BORDER}`,
        borderLeft: `3px solid ${meta.accent}`,
        borderRadius: 10,
        padding: '0.85rem 1rem',
        fontFamily: "'Raleway', sans-serif",
        fontSize: '0.95rem',
        cursor: hasTarget ? 'pointer' : 'default',
        transition: 'background 0.12s',
      }}
      onMouseEnter={hasTarget ? e => { e.currentTarget.style.background = '#f7f9fc'; } : undefined}
      onMouseLeave={hasTarget ? e => { e.currentTarget.style.background = '#fff'; } : undefined}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }}>{meta.icon}</span>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, color: NAVY, fontWeight: 600, lineHeight: 1.45 }}>
            {prompt.description}
          </p>
          {prompt.suggestion && (
            <p style={{ margin: '0.2rem 0 0', color: '#666', fontSize: '0.9rem', lineHeight: 1.4 }}>
              {prompt.suggestion}
            </p>
          )}
        </div>
        {hasTarget && (
          <span style={{ fontSize: '0.75rem', color: '#bbb', flexShrink: 0, alignSelf: 'center' }}>↗</span>
        )}
      </div>

      {/* Actions */}
      <div
        onClick={stopProp}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.65rem' }}
      >
        {/* Primary */}
        <button
          onClick={handlePrimary}
          style={{
            padding: '0.35rem 0.85rem',
            background: NAVY, color: '#fff',
            border: 'none', borderRadius: 6,
            fontSize: '0.85rem', fontWeight: 700,
            cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
          }}
        >
          {prompt.primaryAction.label}
        </button>

        {/* Secondary (proposal only — "Mark as sent") */}
        {prompt.secondaryAction && (
          <button
            onClick={handleSecondary}
            style={{
              padding: '0.35rem 0.85rem',
              background: 'transparent',
              border: `1px solid ${BORDER}`,
              borderRadius: 6,
              fontSize: '0.85rem', fontWeight: 600, color: '#555',
              cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
            }}
          >
            {prompt.secondaryAction.label}
          </button>
        )}

        {/* Dismiss */}
        <button
          onClick={e => { stopProp(e); onDismiss(); }}
          style={{
            padding: '0.35rem 0.7rem',
            background: 'transparent',
            border: `1px solid ${BORDER}`,
            borderRadius: 6,
            fontSize: '0.85rem', color: '#999',
            cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
          }}
        >
          Dismiss for now
        </button>

        {/* Snooze */}
        {!confirming && (
          <button
            onClick={e => { stopProp(e); setConfirming(true); }}
            style={{
              background: 'none', border: 'none',
              fontSize: '0.85rem', color: '#bbb',
              cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
              textDecoration: 'underline', padding: 0,
            }}
          >
            Remind me tomorrow
          </button>
        )}
        {confirming && (
          <span style={{ fontSize: '0.85rem', color: '#888' }}>
            Remind tomorrow?{' '}
            <button
              onClick={e => { stopProp(e); onSnooze(); setConfirming(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: NAVY, fontWeight: 700, fontSize: '0.85rem', fontFamily: "'Raleway', sans-serif" }}
            >
              Yes
            </button>
            {' / '}
            <button
              onClick={e => { stopProp(e); setConfirming(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '0.85rem', fontFamily: "'Raleway', sans-serif" }}
            >
              No
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
